import React, { useEffect, useState } from 'react';
import { Box, Divider, IconButton, Paper, styled, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, useTheme } from '@mui/material';
import './MenuCommon.css';
import { getSelectItemsAPI } from 'apis/settings/settingsAPI';
import ItemPopup from './ItemManagementPopup/ItemPopup';
import { PlaylistAdd } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

export interface Item {
  id: number;
  name: string;
  nameEnglish: string;
  nameJapanese: string;
}

interface ItemTableProps {
  title: string; // 구분자 : 사업장 혹은 디지털기기
  items: Item[];
  handleOpenPopup: any;
}

const StyledTableRow = styled(TableRow)({
  '&:hover': {
    backgroundColor: 'rgba(173, 216, 230, 0.2)',
    // outline: '1px solid rgba(0, 0, 255, 0.3)',
    // outlineOffset: '-1px',
    cursor: 'pointer',
    '& td': { color: 'rgba(73, 116, 230, 1)' }
  }
});

const ItemTable: React.FC<ItemTableProps> = ({ title, items, handleOpenPopup }) => {
  // 다국어
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'ko');
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용

  // 다크 라이트 모드
  const theme = useTheme();
  const StyledTableCell = styled(TableCell)({
    borderRight: '1px solid',
    borderColor: theme.palette.table.border,
    backgroundColor: theme.palette.table.row
  });
  const HiddenStyledTableCell = styled(StyledTableCell)({
    display: 'none',
    userSelect: 'none',
    pointerEvents: 'none'
  });
  const StyledTableHeaderCell = styled(StyledTableCell)({
    backgroundColor: theme.palette.table.header,
    padding: '8px',
    textAlign: 'center',
    fontWeight: 'bold',
    userSelect: 'none',
    pointerEvents: 'none'
  });
  // 추가버튼 클릭 팝업
  const handleOpenAddPopup = (event: any) => {
    handleOpenPopup(title, null, items.length + 1); // 추가 버튼 클릭 시는 아이템 데이터가 없으므로 null 전달
  };

  // 아이템 클릭 팝업
  const handleOpenItemPopup = (clickItemId: number) => {
    const clickItemData = items.find((item) => item.id === clickItemId);
    if (clickItemData) {
      handleOpenPopup(title, clickItemData, null); // 클릭된 아이템 데이터를 전달
    }
  };
  return (
    <Box padding={2} paddingBottom={2} borderRadius={1} bgcolor={theme.palette.contentBlock.background} display="flex" flexDirection="column" gap={2}>
      <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
        <Typography variant="h5" fontWeight="bold">
          {title}
        </Typography>

        <IconButton size="small" color="primary" onClick={handleOpenAddPopup} sx={{ padding: '2px' }}>
          <PlaylistAdd sx={{ fontSize: '24px' }} />
        </IconButton>
      </Box>
      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <HiddenStyledTableCell>ID</HiddenStyledTableCell>
              <StyledTableHeaderCell>{t('item_management.item_table.name_ko')}</StyledTableHeaderCell>
              <StyledTableHeaderCell>{t('item_management.item_table.name_en')}</StyledTableHeaderCell>
              <StyledTableHeaderCell>{t('item_management.item_table.name_ja')}</StyledTableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <StyledTableRow key={item.id} onClick={() => handleOpenItemPopup(item?.id)}>
                <HiddenStyledTableCell>{item.id}</HiddenStyledTableCell>
                <StyledTableCell>{item.name}</StyledTableCell>
                <StyledTableCell>{item.nameEnglish}</StyledTableCell>
                <StyledTableCell>{item.nameJapanese}</StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default function ItemManagement() {
  const [openPopup, setOpenPopup] = useState(false); // 팝업 열고닫는 상태관리
  const [popupTitle, setPopupTitle] = useState<string | null>(null); // 팝업 제목 관리
  const [itemListLength, setItemListLength] = useState<number | null>(0); // 팝업 항목 길이
  const [businessItems, setBusinessItems] = useState<Item[]>([]); // 사업장 데이터
  const [deviceItems, setDeviceItems] = useState<Item[]>([]); // 디지털 기기 데이터
  const [selectedItem, setSelectedItem] = useState<Item | null>(null); // 클릭한 아이템 데이터 관리
  // 다크 라이트 모드
  const theme = useTheme();
  // 다국어
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'ko');
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용

  const fetchSettings = async () => {
    try {
      const selectItemsResponse = await getSelectItemsAPI();
      const { businessPlaces, digitalDevices } = selectItemsResponse.data;
      // console.log('selectItemsResponse : ', selectItemsResponse.data);
      // 데이터가 있으면 Editor에 설정
      setBusinessItems(businessPlaces);
      setDeviceItems(digitalDevices);
    } catch (error) {
      // console.error('데이터 가져오기 실패 :', error);
      console.error(error);
    }
  };

  // 데이터 가져오기 (컴포넌트 첫 마운트 시)
  useEffect(() => {
    fetchSettings();
  }, []); // 빈 배열이므로 처음 마운트될 때만 실행

  // 팝업 열기 및 닫기 함수 (title 및 itemData )
  const handleOpenPopup = (title: string, item: Item | null, itemListLength: number | null) => {
    setSelectedItem(item); // 클릭된 아이템 데이터 설정 (없으면 null)
    setPopupTitle(title); // title 설정
    setItemListLength(itemListLength);
    setOpenPopup(true); // 팝업 열기
  };

  const handleClosePopup = () => {
    setSelectedItem(null); // 선택된 아이템 초기화
    setPopupTitle(null); // title 초기화
    setOpenPopup(false); // 팝업 닫기

    fetchSettings();
  };

  // 삭제 처리 함수
  const handleDeleteItem = (itemId: number) => {
    if (popupTitle === t('item_management.item_table.business.title')) {
      setBusinessItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
    } else if (popupTitle === t('item_management.item_table.device.title')) {
      setDeviceItems((prevItems) => prevItems.filter((item) => item.id !== itemId));
    }
  };

  // 목록 업데이트 함수 (추가 또는 변경)
  const updateItems = (updatedItem: Item, title: string) => {
    const updateList = (prevItems: Item[]): Item[] => {
      const index = prevItems.findIndex((item) => item.id === updatedItem.id);
      if (index !== -1) {
        // 기존 아이템을 업데이트
        const updatedItems = [...prevItems];
        updatedItems[index] = updatedItem;
        return updatedItems;
      } else {
        // 새 아이템을 추가
        return [...prevItems, updatedItem];
      }
    };

    if (title === t('item_management.item_table.business.title')) {
      setBusinessItems(updateList);
    } else if (title === t('item_management.item_table.device.title')) {
      setDeviceItems(updateList);
    }
  };

  return (
    <Box padding={2} display="flex" flexDirection="column" gap={2}>
      <Box
        // padding={2}
        // paddingBottom={2}
        borderRadius={1}
        // bgcolor={theme.palette.mode === 'dark' ? '#333' : '#f8f8f8'}
        display="flex"
        flexDirection="column"
        gap={1}
      >
        <Typography variant="h5" fontWeight="bold">
          {t('item_management.title')}
        </Typography>

        <Divider flexItem />
      </Box>

      {/* 사업장 테이블 */}
      <ItemTable title={t('item_management.item_table.business.title')} items={businessItems} handleOpenPopup={handleOpenPopup} />
      {/* 디지털기기 테이블 */}
      <ItemTable title={t('item_management.item_table.device.title')} items={deviceItems} handleOpenPopup={handleOpenPopup} />
      {/* 항목관리 팝업 */}
      <ItemPopup
        isOpen={openPopup}
        handleClose={handleClosePopup}
        handleDeleteItem={handleDeleteItem}
        updateItems={updateItems}
        title={popupTitle || ''}
        itemData={selectedItem}
        itemListLength={itemListLength}
      />
    </Box>
  );
}
